import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, FileStack, Check } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

// Dir 35 — cockpit visibility chip: shows which package the engine will produce for a FEDERAL deal
// (IFB / price-only RFQ = cover-only · RFP / technical = a full technical volume) with a one-click
// officer override (Auto / Force technical / Force cover-only).
const MODES = [
  { v: "auto", label: "Auto (detect from Section L/M)", hint: "Let the engine decide from the solicitation." },
  { v: "technical", label: "Force technical volume (RFP)", hint: "Produce a full multi-page technical proposal." },
  { v: "cover", label: "Force cover-only (IFB / price-only)", hint: "Transmittal cover + forms + priced schedule only." },
];

export function AcquisitionPathChip({ deal, isOfficer, reload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (deal.bid_type !== "Federal" || deal.requires_technical_volume === undefined) return null;

  const isTech = !!deal.requires_technical_volume;
  const override = deal.acquisition_override; // true / false / null
  const pathInfo = deal.acquisition_path_reason || {};
  const path = pathInfo.path || (isTech ? "RFP" : "IFB"); // live classification result
  const reason = pathInfo.reason;
  const also = pathInfo.also_detected || []; // all other detected signals (for the hover tooltip)
  const tone = isTech
    ? "bg-indigo-100 text-indigo-800 border-indigo-300"
    : "bg-sky-100 text-sky-800 border-sky-300";

  const setMode = async (mode) => {
    setBusy(true);
    try {
      await api.post(`/bids/${deal.id}/acquisition-path`, { mode });
      toast.success(mode === "auto" ? "Reverted to auto-detection"
        : mode === "technical" ? "Forced: technical volume" : "Forced: cover-only");
      setOpen(false); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update path");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 ml-2 relative inline-block align-top" data-testid="acquisition-path-chip">
      <button
        onClick={() => isOfficer && setOpen((o) => !o)}
        disabled={!isOfficer}
        className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${tone} ${isOfficer ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
        title="Which package the engine will produce (branch on acquisition type)"
        data-testid="acquisition-path-trigger">
        {isTech ? <FileStack className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
        {isTech ? "RFP · Technical volume" : "IFB · Cover-only"}
        {override !== null && override !== undefined && (
          <span className="text-[9px] font-bold uppercase opacity-70">· override</span>
        )}
      </button>

      {reason && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="mt-1 text-[10px] text-zinc-500 max-w-[300px] leading-snug inline-block cursor-help border-b border-dotted border-zinc-300"
                data-testid="acquisition-path-reason">
                <span className="font-semibold text-zinc-700">{path}</span> — {reason}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start"
              className="max-w-[320px] bg-zinc-900 text-zinc-50 text-[11px] leading-snug"
              data-testid="acquisition-path-tooltip">
              {also.length > 0 ? (
                <div>
                  <div className="font-semibold mb-1">Also detected in the solicitation</div>
                  <ul className="list-disc pl-4 space-y-0.5" data-testid="acquisition-path-signals">
                    {also.map((s, i) => (<li key={i}>{s}</li>))}
                  </ul>
                </div>
              ) : (
                <span>No other evaluation signals detected — the classification rests on the reason shown.</span>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {open && (
        <div className="absolute z-20 mt-1 w-64 rounded-lg border border-zinc-200 bg-white shadow-lg p-2" data-testid="acquisition-path-menu">
          <div className="text-[10px] uppercase tracking-wide text-zinc-400 px-1 pb-1">Package path</div>
          {MODES.map((m) => {
            const active = (m.v === "auto" && (override === null || override === undefined))
              || (m.v === "technical" && override === true)
              || (m.v === "cover" && override === false);
            return (
              <button key={m.v} onClick={() => setMode(m.v)} disabled={busy}
                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-zinc-50 flex items-start gap-1.5 disabled:opacity-50"
                data-testid={`acquisition-path-mode-${m.v}`}>
                <Check className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${active ? "text-emerald-600" : "text-transparent"}`} />
                <span className="min-w-0">
                  <span className="block text-[12px] text-zinc-800">{m.label}</span>
                  <span className="block text-[10px] text-zinc-400">{m.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AcquisitionPathChip;
